// Scenes data layer + MQTT-message helpers.
//
// A "Scene" is a named, programmable room macro: tap it and it fires a batch of
// MQTT commands to that room's devices. We DON'T add backend tables â€” a scene
// rides on the existing Wall Mode tables (WallPanel / WallButton / WallButtonAction),
// which already model "a button that runs a sequence of mqtt steps" and whose
// runtime executor (wallApi.runSequence) we reuse verbatim.
//
// Convention (see docs/07-scenes.md):
//   - one hidden WallPanel per room, named `__scene__<RoomID>` â€” the container.
//   - each scene    = one WallButton in that panel  (ButtonText = name). Max 8.
//   - each device   = one WallButtonAction row      (UniqueNumber + MqttMessage).
// Wall Mode ignores `__scene__*` panels (filtered in wallApi.fetchPanels).
//
// OWNERSHIP - the panel is stored under the ROOM'S OWNER, not the logged-in
// user. Every wall endpoint is keyed by UserId, so storing under the current
// user gave each person a private copy of the room's scenes. Reading and writing
// as the Location's MasterUserId makes ONE shared set that everyone with access
// to the room sees. Hence `ownerId` below - pass the value from
// core/scenes/sceneOwner.resolveSceneOwner(), NOT the logged-in userId, or the
// per-user silo comes straight back.
//
// runScene() still publishes as the ACTING user: the MQTT payload records who
// pressed the button, which is a different question from where the scene lives.

import {
  getWallPanels, addWallPanel,
  addWallButton, updateWallButton, deleteWallButton,
  addWallButtonAction, deleteWallButtonActionsByButtonId,
} from '../../../../core/api/api';
import { normalizePanels } from '../wall/wallApi';
import { deviceTypeKey } from '../../../../core/devices/deviceMeta';
import { publishCommand } from '../../../../core/utils/deviceController';

export const SCENE_PANEL_PREFIX = '__scene__';
export const MAX_SCENES_PER_ROOM = 8;

export const scenePanelName = (roomId) => `${SCENE_PANEL_PREFIX}${roomId}`;
export const isScenePanel = (panel) =>
  typeof panel?.PanelName === 'string' && panel.PanelName.startsWith(SCENE_PANEL_PREFIX);

const insertIdOf = (res) => {
  const d = res && res.data;
  const m = Array.isArray(d) ? d[0] : d;
  return m && (m.insertId != null) ? Number(m.insertId) : null;
};

// -------------------------------------------------------------------------
// Device -> which target states a scene can set, and the MQTT message each maps
// to. Mirrors the control components exactly (TouchScreen / FanDial / LightScreen
// / CurtainScreen). Door locks + energy meters are intentionally omitted from
// scenes (never auto-unlock; meters are read-only).
// -------------------------------------------------------------------------

const FAN_OPTIONS = [
  { key: 'off', label: 'Off', message: '1-10' },
  { key: 'sp1', label: 'SP1', message: '1-20' },
  { key: 'sp2', label: 'SP2', message: '1-40' },
  { key: 'sp3', label: 'SP3', message: '1-60' },
  { key: 'sp4', label: 'SP4', message: '1-80' },
  { key: 'full', label: 'Full', message: '1-100' },
];

const CURTAIN_OPTIONS = [
  { key: 'open', label: 'Open', message: 'OPENCURTAIN' },
  { key: 'close', label: 'Close', message: 'CLOSECURTAIN' },
];

const lightOptions = (channels) => {
  const off = channels.map(() => 0).join(',');
  if (channels.length === 2) { // white / warm
    return [
      { key: 'white', label: 'White', message: '255,0' },
      { key: 'warm', label: 'Warm', message: '0,255' },
      { key: 'cool', label: 'Cool', message: '128,128' },
      { key: 'off', label: 'Off', message: off },
    ];
  }
  if (channels.length === 5) { // w / ww / r / g / b
    return [
      { key: 'white', label: 'White', message: '255,0,0,0,0' },
      { key: 'warm', label: 'Warm', message: '0,255,0,0,0' },
      { key: 'red', label: 'Red', message: '0,0,255,0,0' },
      { key: 'green', label: 'Green', message: '0,0,0,255,0' },
      { key: 'blue', label: 'Blue', message: '0,0,0,0,255' },
      { key: 'off', label: 'Off', message: off },
    ];
  }
  return [ // r / g / b
    { key: 'red', label: 'Red', message: '255,0,0' },
    { key: 'green', label: 'Green', message: '0,255,0' },
    { key: 'blue', label: 'Blue', message: '0,0,255' },
    { key: 'off', label: 'Off', message: '0,0,0' },
  ];
};

// Which device types need their getOutputs() rows to build controls: multi-gang
// switches (for gang names) and generic/unsupported devices (their outputs ARE
// the controls, e.g. sirens, bulb adaptors, IR-blaster switches).
export function needsOutputs(device) {
  const key = deviceTypeKey(device.DeviceID);
  return key === 'twoTouch' || key === 'fourTouch' || key === 'unsupported';
}

// Returns the list of independently-settable "controls" for one device. Each
// control is one MQTT message a scene step can carry. Multi-gang switches expand
// to one control per gang; generic devices expand to one On/Off per output;
// single-message devices (fan/curtain/light) return one. `outputs` is the
// getOutputs() rows (required for multi-gang names and generic devices).
export function deviceControls(device, outputs = []) {
  const key = deviceTypeKey(device.DeviceID);
  const uno = device.UniqueNumber;
  const deviceName = device.DeviceName || uno;
  const nameOf = (gang) => {
    const o = outputs.find((x) => Number(x.Output) === gang);
    return (o && o.OutputName) || `Switch ${gang}`;
  };
  const gangs = key === 'fourTouch' ? 4 : key === 'twoTouch' ? 2 : key === 'oneTouch' ? 1 : 0;

  if (gangs > 0) {
    return Array.from({ length: gangs }, (_, i) => {
      const gang = i + 1;
      return {
        id: `${uno}#${gang}`,
        uniqueNumber: uno,
        deviceName,
        label: gangs > 1 ? nameOf(gang) : deviceName,
        binary: true, // on/off â€” always saved explicitly (default off)
        options: [
          { key: 'on', label: 'On', message: `${gang}-1` },
          { key: 'off', label: 'Off', message: `${gang}-0` },
        ],
      };
    });
  }
  if (key === 'fan') {
    return [{ id: `${uno}#1`, uniqueNumber: uno, deviceName, label: deviceName, binary: false, options: FAN_OPTIONS }];
  }
  if (key === 'curtain') {
    return [{ id: `${uno}#1`, uniqueNumber: uno, deviceName, label: deviceName, binary: false, options: CURTAIN_OPTIONS }];
  }
  if (key === 'rgb' || key === 'wwh' || key === 'wwrgb') {
    // Dimmable light: a scene stores an exact per-channel value set (0-255 each),
    // edited with the same sliders as the main light page â€” not fixed presets.
    // `kind: 'light'` tells the editor to render channel sliders; the step's MQTT
    // message is the comma-joined values (e.g. "128,0,255"). Presets are kept for
    // reference/quick-set.
    const channels = key === 'rgb' ? ['R', 'G', 'B'] : key === 'wwh' ? ['W', 'WH'] : ['W', 'WW', 'R', 'G', 'B'];
    return [{
      id: `${uno}#1`, uniqueNumber: uno, deviceName, label: deviceName,
      binary: false, kind: 'light', channels, options: lightOptions(channels),
    }];
  }
  if (key === 'unsupported') {
    // Generic device: one On/Off control per firmware output (like UnsupportedScreen).
    return outputs.map((o) => ({
      id: `${uno}#${o.Output}`,
      uniqueNumber: uno,
      deviceName,
      label: o.OutputName || `Output ${o.Output}`,
      binary: true, // on/off â€” always saved explicitly (default off)
      options: [
        { key: 'on', label: 'On', message: `${o.Output}-1` },
        { key: 'off', label: 'Off', message: `${o.Output}-0` },
      ],
    }));
  }
  return []; // door / meters -> not scene-programmable
}

// True if a device exposes anything a scene can set. Pass its outputs so generic
// devices (whose controls come from outputs) are judged correctly.
export const isSceneable = (device, outputs = []) => deviceControls(device, outputs).length > 0;

// Map a device's live/last-known status string to per-control option keys, so a
// NEW scene can be seeded with the current state of every output (a full
// snapshot). `status` is the parsed status (substring between ']' and '#'),
// e.g. switch '0,1,0,1' or '1-1', fan '1-60', light '255,0,0'. Returns
// { [controlId]: optionKey } for whatever we can resolve; controls we can't map
// (offline / no persistent status like curtains) are simply omitted.
export function statusSelections(device, status, outputs = []) {
  const key = deviceTypeKey(device.DeviceID);
  const uno = device.UniqueNumber;
  const sel = {};
  if (!status) return sel;

  if (key === 'unsupported') {
    const parts = status.split(',');
    outputs.forEach((o, i) => { sel[`${uno}#${o.Output}`] = parts[i] === '1' ? 'on' : 'off'; });
    return sel;
  }

  const gangs = key === 'fourTouch' ? 4 : key === 'twoTouch' ? 2 : key === 'oneTouch' ? 1 : 0;
  if (gangs > 0) {
    let states;
    if (gangs === 1) {
      const parts = status.split('-');
      states = [parts[parts.length - 1] !== '0'];
    } else {
      const parts = status.split(',');
      states = Array.from({ length: gangs }, (_, i) => parts[i] === '1');
    }
    states.forEach((on, i) => { sel[`${uno}#${i + 1}`] = on ? 'on' : 'off'; });
    return sel;
  }
  if (key === 'fan') {
    const parts = status.split('-');
    const speed = Number(parts[parts.length - 1]);
    const opt = FAN_OPTIONS.find((o) => o.message === `1-${speed}`);
    if (opt) sel[`${uno}#1`] = opt.key;
    else if (!Number.isNaN(speed) && speed <= 10) sel[`${uno}#1`] = 'off';
    return sel;
  }
  if (key === 'rgb' || key === 'wwh' || key === 'wwrgb') {
    // Seed a new scene with the light's exact current colour (raw channel values),
    // so the editor's sliders open on the real state.
    const count = key === 'rgb' ? 3 : key === 'wwh' ? 2 : 5;
    const norm = status.split(',').map((n) => Number(n)).slice(0, count);
    if (norm.length === count && norm.every((n) => !Number.isNaN(n))) {
      sel[`${uno}#1`] = norm.join(',');
    }
    return sel;
  }
  return sel; // curtain / door / meter â€” no persistent status to snapshot
}

// -------------------------------------------------------------------------
// CRUD â€” maps scenes onto the wall tables.
// -------------------------------------------------------------------------

// Find the hidden scene panel for a room (or null). Uses the same nested fetch
// Wall Mode uses; getWallPanels returns ALL of the user's panels.
async function findScenePanel(ownerId, roomId) {
  const res = await getWallPanels(ownerId);
  const panels = normalizePanels(res.data);
  return panels.find((p) => p.PanelName === scenePanelName(roomId)) || null;
}

// Ensure the room's container panel exists; returns its WallPanelId.
async function ensureScenePanel(ownerId, roomId) {
  const existing = await findScenePanel(ownerId, roomId);
  if (existing) return existing.WallPanelId;
  const res = await addWallPanel({
    UserId: ownerId,
    PanelName: scenePanelName(roomId),
    SortOrder: 0,
    BackColor: '#101820',
    ColumnsCount: 4,
  });
  return insertIdOf(res);
}

// List a room's scenes. Each scene: { id, name, color, textColor, sortOrder,
// steps: [{ uniqueNumber, message }] }.
function scenesOfPanel(panel) {
  return (panel.Buttons || []).map((b) => ({
    id: b.WallButtonId,
    name: b.ButtonText || 'Scene',
    color: b.ButtonColor || '#37474F',
    textColor: b.TextColor || '#FFFFFF',
    sortOrder: b.SortOrder || 0,
    steps: (b.Actions || [])
      .filter((a) => a.UniqueNumber && a.MqttMessage)
      .map((a) => ({ uniqueNumber: a.UniqueNumber, message: a.MqttMessage })),
  })).sort((a, b) => a.sortOrder - b.sortOrder);
}

export async function fetchRoomScenes(ownerId, roomId) {
  const panel = await findScenePanel(ownerId, roomId);
  if (!panel) return [];
  return scenesOfPanel(panel);
}

// Every room's scenes for one owner, as { [roomId]: scenes[] }.
//
// getWallPanels returns the owner's panels WITH their buttons and actions
// nested, so this is ONE request for a whole location. Listing scenes room by
// room would re-fetch that same payload once per room.
export async function fetchScenesByRoom(ownerId) {
  const res = await getWallPanels(ownerId);
  const panels = normalizePanels(res.data);
  const byRoom = {};
  panels.filter(isScenePanel).forEach((p) => {
    const roomId = String(p.PanelName).slice(SCENE_PANEL_PREFIX.length);
    byRoom[roomId] = scenesOfPanel(p);
  });
  return byRoom;
}

// Create or update a scene. `scene` = { id?, name, color?, textColor?, sortOrder?,
// steps: [{ uniqueNumber, message }] }. Replaces the whole step set on save
// (delete-all + re-insert), exactly like Wall Mode buttons. Returns the scene id.
export async function saveScene(ownerId, roomId, scene) {
  const panelId = await ensureScenePanel(ownerId, roomId);
  if (!panelId) return null;

  const col = {
    WallPanelId: panelId,
    // The button belongs to the same account as its panel, or the nested fetch
    // in getWallPanels(ownerId) would not return it.
    UserId: ownerId,
    ButtonText: scene.name || 'Scene',
    ButtonColor: scene.color || '#37474F',
    TextColor: scene.textColor || '#FFFFFF',
    IconName: '',
    ButtonType: 'action',
    TargetWallPanelId: 0,
    Width: 1,
    Height: 1,
    PosX: 0,
    PosY: 0,
    SortOrder: scene.sortOrder || 0,
  };

  let sceneId = scene.id;
  if (!sceneId) {
    sceneId = insertIdOf(await addWallButton(col));
  } else {
    await updateWallButton(col, `WallButtonId = ${sceneId}`);
  }
  if (!sceneId) return null;

  await deleteWallButtonActionsByButtonId(sceneId);
  const steps = scene.steps || [];
  for (let i = 0; i < steps.length; i++) {
    const s = steps[i];
    // eslint-disable-next-line no-await-in-loop
    await addWallButtonAction({
      WallButtonId: sceneId,
      StepOrder: i + 1,
      ActionType: 'mqtt',
      UniqueNumber: s.uniqueNumber,
      MqttMessage: s.message,
      DelaySeconds: 0,
    });
  }
  return sceneId;
}

export const deleteScene = (sceneId) =>
  deleteWallButtonActionsByButtonId(sceneId)
    .then(() => deleteWallButton(`WallButtonId = ${sceneId}`));

// Delay between consecutive scene messages. Firmware/broker can't process a burst
// of publishes fired back-to-back, so we space them out.
export const SCENE_STEP_DELAY_MS = 350;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Run a scene: publish every step's MQTT message to its device, spaced by
// SCENE_STEP_DELAY_MS so each command is actually acted on.
export async function runScene(userId, scene) {
  const steps = (scene.steps || []).filter((s) => s.uniqueNumber && s.message);
  for (let i = 0; i < steps.length; i++) {
    publishCommand(steps[i].uniqueNumber, steps[i].message, userId);
    if (i < steps.length - 1) await sleep(SCENE_STEP_DELAY_MS); // eslint-disable-line no-await-in-loop
  }
}
